import { useCallback } from "react";
import {
  CustomFieldModal,
  type CustomFieldModalProps,
  type CustomFieldPayload,
  type CustomFieldInitialValues,
} from "../../../../../../../../../components/CustomFieldModal/CustomFieldModal";
import {
  FIELD_OPTIONS,
  UI_TO_API_FIELD_TYPE,
} from "../../../../CreateTicket/utils/constants";
import type { CustomFieldType } from "../../../../CreateTicket/utils/types";
import {
  useCreateHelpdeskCustomFieldMutation,
  useUpdateHelpdeskCustomFieldMutation,
} from "../../../../../../../../../redux/services/helpdeskApi";
import { customToast } from "../../../../../../../../../utils/custom-toast/custom-toast";
import type { HelpdeskCustomFieldDefinition } from "../utils/helpdeskCustomFields";

const API_TO_UI_FIELD_TYPE: Record<string, CustomFieldType> = {
  dropdown_single_select: "dropdown",
  dropdown_multi_select: "dropdown-multi",
  text: "text",
  long_text: "long-text",
  radio_single_select: "radio",
  checkbox: "checkbox",
  number: "number",
  date: "date",
};

export function fieldDefToEditValues(field: HelpdeskCustomFieldDefinition): {
  fieldType: CustomFieldType;
  initialValues: CustomFieldInitialValues;
} {
  const uiType = API_TO_UI_FIELD_TYPE[field.type] ?? "text";
  return {
    fieldType: uiType,
    initialValues: {
      fieldName: field.name,
      fieldType: uiType,
      description: field.description ?? "",
      options: field.values_for_dropdown ?? [],
      isRequired: field.is_required,
      filterRequired: field.show_in_filter,
      ...(uiType === "checkbox"
        ? {
            checkboxDisplayStyle:
              field.checkbox_display_style === "cards" ? "cards" : "chips",
          }
        : {}),
    },
  };
}

export interface HelpdeskTicketCustomFieldModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fieldType: CustomFieldType | null;
  onSuccess?: () => void;
  editingField?: HelpdeskCustomFieldDefinition | null;
}

export function HelpdeskTicketCustomFieldModal({
  open,
  onOpenChange,
  fieldType,
  onSuccess,
  editingField,
}: HelpdeskTicketCustomFieldModalProps) {
  const [createHelpdeskCustomField] = useCreateHelpdeskCustomFieldMutation();
  const [updateHelpdeskCustomField] = useUpdateHelpdeskCustomFieldMutation();

  const isEditing = Boolean(editingField);
  const editValues = editingField ? fieldDefToEditValues(editingField) : null;

  const effectiveFieldType = isEditing ? editValues!.fieldType : fieldType;

  const handleAddField = useCallback(
    async (payload: CustomFieldPayload) => {
      const apiType =
        UI_TO_API_FIELD_TYPE[payload?.fieldType as CustomFieldType];
      const hasOptions = payload?.options?.length > 0;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const body: any = {
        name: payload?.label,
        type: apiType,
        show_in_filter: payload?.filterRequired,
        is_required: payload?.isRequired,
        client_code: null,
      };

      if (payload?.description) {
        body.description = payload?.description;
      }

      if (hasOptions) {
        body.values_for_dropdown = payload?.options;
      }

      if (apiType === "checkbox" && hasOptions) {
        body.checkbox_display_style =
          payload?.checkboxDisplayStyle === "cards" ? "cards" : "chips";
      }

      try {
        if (isEditing && editingField) {
          await updateHelpdeskCustomField({
            key: editingField.key,
            ...body,
          })?.unwrap();
          customToast("Custom field updated successfully", "success");
        } else {
          await createHelpdeskCustomField(body)?.unwrap();
          customToast("Custom field created successfully", "success");
        }
        onSuccess?.();
      } catch (err: unknown) {
        const fallback = isEditing
          ? "Failed to update custom field"
          : "Failed to create custom field";
        const detail =
          (err as { data?: { detail?: string } })?.data?.detail || fallback;
        customToast(detail, "error");
        throw err;
      }
    },
    [
      createHelpdeskCustomField,
      updateHelpdeskCustomField,
      onSuccess,
      isEditing,
      editingField,
    ]
  );

  return (
    <CustomFieldModal
      open={open}
      onOpenChange={onOpenChange}
      fieldType={effectiveFieldType}
      fieldOptions={FIELD_OPTIONS}
      onAddField={handleAddField}
      title={isEditing ? "Edit Custom Field" : "Add Custom Field"}
      submitText={isEditing ? "Update Field" : "Save Changes"}
      initialValues={editValues?.initialValues ?? null}
    />
  );
}

export {
  CustomFieldModal,
  type CustomFieldModalProps,
  type CustomFieldPayload,
};
